import { createContext, useContext, type ReactNode } from 'react';

/**
 * Doodle upload is the one piece of doodle behavior the renderer always
 * delegates to the host: the doodle modal rasterizes the strokes to a PNG
 * `File`, but turning that into a hosted URL (so the attachment image block
 * can render it and the backend can persist it) requires the app's upload
 * pipeline. Kaleidoscope wires this to the same ActionInput `onUpload`
 * attachment path Frederick's in-place doodle uses.
 *
 * Mirrors the link/gif/analytics seams: a context + a default. The default
 * rejects so a misconfigured host fails loudly rather than silently dropping
 * the doodle (there is no safe no-op URL).
 */
export type BizyDoodleUpload = (png: File) => Promise<{ url: string }>;

const defaultUpload: BizyDoodleUpload = async () => {
	throw new Error('No doodle upload handler provided to the Bizy DocumentRenderer.');
};

const BizyDoodleUploadContext = createContext<BizyDoodleUpload>(defaultUpload);

export function BizyDoodleUploadProvider({ uploadDoodle, children }: { uploadDoodle?: BizyDoodleUpload; children: ReactNode }) {
	return <BizyDoodleUploadContext.Provider value={uploadDoodle ?? defaultUpload}>{children}</BizyDoodleUploadContext.Provider>;
}

export function useBizyDoodleUpload(): BizyDoodleUpload {
	return useContext(BizyDoodleUploadContext);
}
